import { useState } from 'react';
import { CONTACT_METHOD_KINDS, Color, normalizeEmail, Size, t, takesExtension, validateEmail, type ContactMethodKind } from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Button, Checkbox, PhoneInput, Select, TextInput } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import { del, PAGES_PATH, patch, post } from './api';
import styles from './RecordPage.module.css';

/**
 * The Contact details tile's + Add and its pencil (piece 13, decision 0170):
 * one dated row per way of reaching a person at one employment. 🔨 The
 * extension is its own field, kept apart from the value for the dialer, and
 * it belongs to a desk line alone: a mobile, a fax and an address carry none,
 * so the field is not shown for them (product owner, 2026-09-12). The row
 * belongs to the person's most recently started active employment, the one
 * the page's top shows. A pencil corrects an open row in place and never its
 * kind; a line that changed is a new row and the old one is removed.
 *
 * 🔨 Removing a line is done from here since decision 0589, and the row's own
 * × is gone: a removal one press from the row is a deletion nobody confirmed
 * (product owner, 2026-09-20). It is the same end route, behind a confirmation
 * that says the line is end-dated rather than deleted.
 */
export interface ContactMethodEdit {
  id: string;
  kind: ContactMethodKind;
  value: string;
  extension: string | null;
  /** The row's own word for the line: work, mobile, desk (T147, 2026-09-21). */
  label: string | null;
  isPrimary: boolean;
  /** The job the line already belongs to, which a correction names and never moves (decision 0593). */
  jobLabel?: string | null;
}

/** One of the person's open jobs, as the picker offers it (decision 0593). */
export interface ContactMethodJob {
  id: string;
  label: string;
  headline: boolean;
}

export interface ContactMethodModalProps {
  open: boolean;
  /** The employment the row is written against; null for a person with none, which the modal says. */
  employmentId: string | null;
  /**
   * 🔨 The person's open jobs. With more than one the window asks which job the
   * line belongs to, opening on the headline one; with one it names that job
   * rather than asking (decision 0593).
   */
  jobs?: readonly ContactMethodJob[];
  /** The row a pencil opened; absent for + Add. */
  initial?: ContactMethodEdit | null;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
  openerId?: string | null;
}

export function ContactMethodModal({ open, employmentId, jobs = [], initial = null, apiBaseUrl = '', onCancel, onSaved, openerId = null }: ContactMethodModalProps) {
  const editing = initial !== null;
  /**
   * 🔨 The job is chosen before the line is typed, and the window opens on the
   * headline job — the one the banner's own Email and Phone read from — so the
   * silent write of decision 0170 becomes a visible choice (decision 0593).
   */
  const [job, setJob] = useState<string>(() => jobs.find((row) => row.headline)?.id ?? jobs[0]?.id ?? employmentId ?? '');
  const jobLabelOf = (id: string): string | null => jobs.find((row) => row.id === id)?.label ?? null;
  const [kind, setKindState] = useState<ContactMethodKind>(initial?.kind ?? 'WorkEmail');
  const [value, setValue] = useState(initial?.value ?? '');
  const [extension, setExtension] = useState(initial?.extension ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [isPrimary, setIsPrimary] = useState(initial?.isPrimary ?? false);
  const [error, setError] = useState<string | null>(null);
  const setKind = (next: ContactMethodKind) => {
    setKindState(next);
    if (!takesExtension(next)) setExtension('');
  };

  /**
   * Remove, from inside the correction (decision 0589): the same end route the
   * row's × called, behind the plain confirmation the Destructive button opens.
   * 🔨 The server end-dates the row rather than deleting it, and the
   * confirmation says so, because nothing is lost and the person should know it.
   */
  const remove = async () => {
    if (!initial) return;
    setError(null);
    const answer = await del(`${PAGES_PATH}/contact-methods/${initial.id}`, apiBaseUrl);
    if (!answer.ok) {
      setError(answer.message);
      return;
    }
    onSaved();
    onCancel();
  };

  const save = async () => {
    setError(null);
    const body = { value: value.trim(), extension: takesExtension(kind) && extension.trim() ? extension.trim() : null, label: label.trim() || null, isPrimary };
    const writeTo = job || employmentId;
    const answer = editing
      ? await patch(`${PAGES_PATH}/contact-methods/${initial.id}`, body, apiBaseUrl)
      : writeTo
        ? await post(`${PAGES_PATH}/employments/${writeTo}/contact-methods`, { kind, ...body }, apiBaseUrl)
        : null;
    if (!answer) return;
    if (!answer.ok) {
      setError(answer.message);
      return;
    }
    setValue('');
    setExtension('');
    setLabel('');
    onSaved();
    onCancel();
  };

  // The job the window names, and the sentence naming it, composed here rather than in the markup.
  const jobNamed = (editing ? initial?.jobLabel : jobLabelOf(job)) ?? null;
  const jobSentence = jobNamed === null ? null : `${t('contactModal.belongsTo')} ${jobNamed}`;

  return (
    <Modal
      open={open}
      title={editing ? t('contactModal.editTitle') : t('contactModal.title')}
      subtitle={editing ? t('contactModal.editSubtitle') : t('contactModal.subtitle')}
      onCancel={onCancel}
      openerId={openerId}
      dataModal={editing ? 'edit-contact-method' : 'add-contact-method'}
      actions={[
        { kind: 'primary', label: editing ? t('contactModal.saveEdit') : t('contactModal.save'), onAct: () => void save(), disabled: (!editing && !(job || employmentId)) || value.trim() === '' },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      <div data-contact-method-modal data-contact-mode={editing ? 'edit' : 'add'}>
        {error && <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} />}
        {!editing && !(job || employmentId) && <AlertBanner tone="warning" message={t('contactModal.noEmployment')} action={{ label: t('modal.cancel'), onAction: onCancel }} />}
        {/* 🔨 The window always says which job the line belongs to, and asks only where
            there is a choice to make (decision 0593). A correction never moves a line
            between jobs: the row is end-dated and written again on the other job. */}
        {!editing && jobs.length > 1 ? (
          <Select size={Size.Medium}
            label={t('contactModal.job')}
            required
            value={job}
            onChange={setJob}
            options={jobs.map((row) => ({ value: row.id, label: row.headline ? `${row.label} · ${t('contactModal.jobHeadline')}` : row.label }))}
            dataAttribute={{ 'data-contact-field': 'job' }}
          />
        ) : jobNamed ? (
          <p className={styles.hint} data-contact-job={jobNamed}>
            {jobSentence}
          </p>
        ) : null}
        <Select size={Size.Medium}
          label={t('contactModal.kind')}
          required
          value={kind}
          onChange={(next) => setKind(next as ContactMethodKind)}
          disabled={editing}
          options={CONTACT_METHOD_KINDS.map((option) => ({ value: option, label: option }))}
          dataAttribute={{ 'data-contact-field': 'kind' }}
        />
        {/* 🔨 The label is the row's own word for the line and the third thing the
            window asks, between the kind and the value it names (T147, product owner,
            2026-09-21). It is free text, it is not required, and the tiles draw it
            beside the kind wherever a line carries one. */}
        <TextInput size={Size.Medium} label={t('contactModal.label')} value={label} onChange={setLabel} placeholder={t('contactModal.labelPlaceholder')} name="contact-label" />
        {/* The value draws its kind (decisions 0290, 0297 and 0340): an email box with the shape validator, the Phone Input with its country and, on a desk line, its extension, or plain text for Other. */}
        {kind === 'WorkEmail' || kind === 'PersonalEmail' ? (
          <TextInput size={Size.Medium} label={t('contactModal.value')} type="email" value={value} onChange={setValue} onBlur={() => setValue((was) => normalizeEmail(was))} validate={(typed) => { if (!typed.trim()) return null; const outcome = validateEmail(typed, 'US'); return outcome.ok ? null : outcome.message; }} required />
        ) : kind === 'Other' ? (
          <TextInput size={Size.Small} label={t('contactModal.value')} value={value} onChange={setValue} required />
        ) : takesExtension(kind) ? (
          <PhoneInput size={Size.Medium} label={t('contactModal.value')} value={value} onChange={setValue} extension={extension} onExtensionChange={setExtension} extensionName="contact-extension" name="contact-value" required />
        ) : (
          <PhoneInput size={Size.Medium} label={t('contactModal.value')} value={value} onChange={setValue} name="contact-value" required />
        )}
        <Checkbox label={t('contactModal.primary')} checked={isPrimary} onChange={setIsPrimary} />
        {editing ? (
          /* 🔨 The Remove is in the Body rather than the Footer, beside the line it
             removes, the way the Item attribute's is (piece 5). */
          <div className={styles.actions} data-contact-remove-block="true">
            <Button
              size={Size.Small}
              color={Color.Destructive}
              text={[t('contactModal.remove')]}
              actions={[() => void remove()]}
              confirm={{ title: t('contactModal.removeTitle'), consequence: { body: t('contactModal.removeConsequence') }, confirmLabel: t('contactModal.removeConfirm') }}
            />
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
